<template>
  <ValidationObserver @submit="createNewSupportQuery">
    <UiModalContainer
      footer-classes="flex justify-end gap-3"
      :modal-show="modal"
      :modal-width="50"
      @handleClick="handleClick"
    >
      <template v-slot:header>
        <span>{{ modalHeading }}</span>
      </template>
      <template v-slot>
        <InputFieldWrapper>
          <InputBox
            v-model="supportQueryData.title"
            type="text"
            name="TITLE"
            title="TITLE"
            label="TITLE"
            placeholder="TITLE"
            rules="required|alpha_numeric_dashes"
          />
          <InputBox
            v-model="supportQueryData.description"
            type="text"
            name="Description"
            title="Description"
            label="Description"
            placeholder="Description"
          />
          <UiSingleSelect
            v-model="supportQueryData.support_query_type_id"
            title="SUPPORT_QUERY"
            rules="required"
            :label="'title'"
            :reduce="'id'"
            :options="supportQueryTypes"
          />
          <UiSingleSelect
            v-model="supportQueryData.support_query_participants[0].user_id"
            title="TEACHER"
            label="full_name"
            class="flex-1"
            :options="teacherList"
            :image="true"
            reduce="id"
            rules="required"
            :search-through-api="true"
            @search="getTeacherList"
          />
          <UiSingleSelect
            v-if="isNotGuardianRole"
            v-model="supportQueryData.support_query_participants[1].user_id"
            title="GUARDIAN"
            label="full_name"
            :options="guardiansList"
            :image="true"
            reduce="id"
            rules="required"
            :search-through-api="true"
            @search="getAllGuardiansList"
          />
          <UiSingleSelect
            v-model="supportQueryData.student_id"
            title="STUDENT"
            label="full_name"
            :image="true"
            reduce="id"
            :options="associatedStudentsList"
          />
        </InputFieldWrapper>
        <InputBox
          v-if="!edit"
          v-model.trim="supportQueryData.message"
          type="textarea"
          name="Message"
          title="MESSAGE"
          class="mt-2"
          rules="required|between:15,1000"
          placeholder="Write"
        />
      </template>
      <template v-slot:footer>
        <div class="flex gap-3 rtl:gap-x-4">
          <UIButton @click="handleClick('close')">Cancel</UIButton>
          <UIButton button="primary" :disabled="isLoading">
            <span v-if="isLoading">
              <Loader />
            </span>
            <span v-else>
              {{ primaryButtonText }}
            </span>
          </UIButton>
        </div>
      </template>
    </UiModalContainer>
  </ValidationObserver>
</template>

<script>
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import InputBox from '@components/UiElements/UiInputBox.vue'
import { mapActions, mapState } from 'vuex'
import Loader from '@components/BaseComponent/Loader.vue'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import { buildWhereQuery } from '@/src/utils/filters'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import { Form as ValidationObserver } from 'vee-validate'
import InputFieldWrapper from '@/src/components/UiElements/InputFieldWrapper.vue'
import EXAM_CONSTANTS from '@src/constants/exam-constants.js'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import { fullName } from '@src/utils/settings/tenant-user.util.js'
export default {
  components: {
    UiModalContainer,
    InputBox,
    ValidationObserver,
    Loader,
    UiSingleSelect,
    UIButton,
    InputFieldWrapper,
  },
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['toggle'],
  data() {
    return {
      TENANT_ROLES,
      EXAM_CONSTANTS,
      supportQueryData: {
        title: '',
        description: '',
        support_query_type_id: '',
        student_id: '',
        support_query_participants: [
          { user_id: '', role: 'section_teacher', is_main: true },
          { user_id: '', role: 'guardian', is_main: true },
        ],
      },
      guardiansList: [],
      teacherList: [],
      associatedStudentsList: [],
      isLoading: false,
      supportQueryTypes: [],
    }
  },
  computed: {
    isNotGuardianRole() {
      return this.activeRole !== TENANT_ROLES.GUARDIAN
    },
    primaryButtonText() {
      return this.edit ? 'Update' : 'Create'
    },
    modalHeading() {
      return this.edit ? 'Edit Support Query' : 'Create Support Query'
    },
    ...mapState({
      activeRole: (state) => state?.layout?.activeRole,
      currentUser: (state) => state?.auth?.currentUser,
      currentCampusScope: (state) => state?.layout?.currentCampusScope,
    }),
    guardianId() {
      if (this.activeRole === TENANT_ROLES.GUARDIAN) {
        return this.currentUser?.guardian_id || null
      }
      const selectedGuardianUserId = this.supportQueryData?.support_query_participants?.[1]?.user_id
      if (!selectedGuardianUserId) return null
      const guardianInfo = this.guardiansList.find(
        (item) =>
          item.id === selectedGuardianUserId || item.guardian_info?.id === selectedGuardianUserId,
      )
      return guardianInfo?.guardian_id || guardianInfo?.guardian_info?.id || null
    },
  },
  watch: {
    'supportQueryData.support_query_participants': {
      handler(newParticipants) {
        const userId = newParticipants[1]?.user_id
        if (userId) {
          this.getAssociatedStudentsList(userId)
        }
      },
      deep: true,
    },
    modalTitle() {
      return this.edit ? 'Edit Support Query Type' : 'Add Support Query Type'
    },
  },
  created() {
    if (this.activeRole === TENANT_ROLES.GUARDIAN) {
      this.getAssociatedStudentsList(this.currentUser.id)
    }
    this.getSupportQueryTypesList()
    this.removeGuardianParticipants()
    if (this.isNotGuardianRole) this.getAllGuardiansList()
    if (this.edit) this.ResetFormData()
    this.getTeacherList()
  },

  methods: {
    removeGuardianParticipants() {
      if (this.activeRole === TENANT_ROLES.GUARDIAN && !this.edit) {
        this.supportQueryData.support_query_participants =
          this.supportQueryData.support_query_participants.filter(
            (participant) => participant.role !== TENANT_ROLES.GUARDIAN,
          )
      }
    },

    getAssociatedStudentsList(id) {
      const guardianId = id || this.guardianId
      if (guardianId) {
        this.getGuardianAssociatedStudents(guardianId).then((res) => {
          this.associatedStudentsList = res?.data?.records || []
        })
      }
    },

    getTeacherList(query = '') {
      const payload = { $where: { ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', query) } }
      payload.$where = {
        ...payload.$where,
        ...buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', this.currentCampusScope?.id),
      }
      this.getTeacherWithFilters(payload).then((response) => {
        this.teacherList = response?.data?.records
        if (this.edit) {
          const teacherMain = this.edit.support_query_participants.find(
            (item) => item.is_main && item.role === TENANT_ROLES.SECTION_TEACHER,
          )
          let existingTeacher = this.teacherList?.find(
            (teacher) => teacher.id === teacherMain?.user?.id,
          )
          if (!existingTeacher && teacherMain?.user) {
            fullName(teacherMain.user)
            this.teacherList.push(teacherMain.user)
          }
        }
      })
    },

    getAllGuardiansList(query = '') {
      let payload = {
        $where: {
          ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', query),
          ...buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', this.currentCampusScope?.id),
        },
      }
      this.getGuardiansWithFilter(payload).then((res) => {
        this.guardiansList = res?.records
        if (this.edit) {
          const guardianMain = this.edit.support_query_participants.find(
            (item) => item.is_main && item.role === TENANT_ROLES.GUARDIAN,
          )
          let existingGuardian = this.guardiansList?.find(
            (guardian) => guardian.id === guardianMain?.user?.id,
          )
          if (!existingGuardian && guardianMain?.user) {
            fullName(guardianMain.user)
            this.guardiansList.push(guardianMain.user)
          }
        }
      })
    },

    async getSupportQueryTypesList() {
      const [res, err] = await this.getSupportQueryTypes()
      if (!err) {
        this.supportQueryTypes = res?.data?.records
      }
    },

    createNewSupportQuery() {
      this.isLoading = true

      this.edit?.support_query_participants?.forEach((participant) => {
        if (participant.is_main && participant.role === TENANT_ROLES.SUPER_ADMIN) {
          this.supportQueryData.support_query_participants.push(participant)
        }
      })

      let payload = { query: this.supportQueryData }
      if (this.edit) {
        payload.id = this.edit.id
      }
      this.createAndUpdateSupportQuery(payload)
        .then(([res, error]) => {
          const errorMessage = error?.message
          if (!errorMessage) {
            this.$emit('toggle', true)
            if (!this.edit) this.redirectToSupportQueryConversationPage(res?.data?.id)
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    redirectToSupportQueryConversationPage(id) {
      this.$router?.push({
        name: 'support-query-conversation',
        params: { id },
        query: { status: 'open' },
      })
    },
    ResetFormData() {
      this.supportQueryData.title = this.edit?.title || ''
      this.supportQueryData.description = this.edit?.description || ''
      this.supportQueryData.support_query_type_id = this.edit?.support_query_type_id || ''
      this.edit?.support_query_participants.forEach((participant) => {
        if (participant.is_main && participant.role === TENANT_ROLES.GUARDIAN) {
          this.supportQueryData.support_query_participants[1].user_id = participant.user.id || ''
        }
        if (participant.is_main && participant.role === TENANT_ROLES.SECTION_TEACHER) {
          this.supportQueryData.support_query_participants[0].user_id = participant.user.id || ''
        }
      })
      this.supportQueryData.student_id = this.edit?.student?.id || ''
    },
    handleClick(eventName) {
      switch (eventName) {
        case 'close':
          this.$emit('toggle')
          break
      }
    },

    ...mapActions('supportQuery', ['getSupportQueryTypes', 'createAndUpdateSupportQuery']),
    ...mapActions('teacher', ['getTeacherWithFilters']),
    ...mapActions('guardian', ['getGuardiansWithFilter', 'getGuardianAssociatedStudents']),
  },
}
</script>
